import isFunction from 'lodash/isFunction';
import pick from 'lodash/pick';
import HereService from '../services/Here';
import constants from '../constants';

const API_KEY = constants.HERE_API_KEY;

const mouseout = evt => {
  evt.target.classList.remove('over');
};

const mouseover = evt => {
  evt.target.classList.add('over');
};

const click = evt => {};

class Map {
  static platform;
  static init() {
    Map.platform = new window.H.service.Platform({
      apikey: API_KEY,
      useHTTPS: true
    });
  }

  hMarkers = [];

  constructor(id, { zoom, onTap, onPointerenter, onMapviewchange, onPointerleave, placeholder, onMapTap, onMapDrag } = {}) {
    this.api = new HereService(API_KEY);
    this.usingApiKey = true;
    this.placeholder = placeholder;

    this.pixelRatio = window.devicePixelRatio || 1;

    this.onTapCallback = onTap;
    this.onPointerenterCallback = onPointerenter;
    this.onPointerleaveCallback = onPointerleave;
    this.onMapviewchange = onMapviewchange;

    this.defaultLayers = Map.platform.createDefaultLayers({
      tileSize: this.pixelRatio === 1 ? 256 : 512,
      ppi: this.pixelRatio === 1 ? 250 : 320,
      lg: 'ita'
    });

    try {
      let layers;
      // INFO vector layer appear slower than raster to users
      if (this.defaultLayers.hasOwnProperty('vector')) {
        layers = this.defaultLayers.vector;
      } else if (this.defaultLayers.hasOwnProperty('raster')) {
        layers = this.defaultLayers.raster;
      } else {
        layers = this.defaultLayers;
      }
      this.map = new window.H.Map(document.getElementById(id), layers.normal.map, { pixelRatio: this.pixelRatio, /* engineType: window.H.map.render.RenderEngine.EngineType.P2D */  });
      this.map.addEventListener('tap', e => {
        this.closeBubbles();
        isFunction(onMapTap) && onMapTap(e);
      });
      this.map.addEventListener('drag', e => {
        isFunction(onMapDrag) && onMapDrag(e);
      });
    } catch (error) {
      console.error(error);
    }

    this.map.addEventListener('mapviewchangeend', e => {
      //Here we should handle events about panning and zooming
      this.currentZoom = this.map.getZoom();
      this.currentViewBounds = this.getViewBounds();
      this.currentCenter = this.map.getCenter();
      if (isFunction(this.onMapviewchange)) {
        this.onMapviewchange(e.target);
      }
    });
    const mapBehavior = new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(this.map));
    mapBehavior.disable(window.H.mapevents.Behavior.Feature.FRACTIONAL_ZOOM);

    this.ui = window.H.ui.UI.createDefault(this.map, this.defaultLayers, 'it-IT');

    this.map.setZoom(zoom || 12);

    this.markersAsObj = {};
  }

  addMarkers(markers = []) {
    if (markers.length > 0) {
      markers.forEach(marker => this.addMarker(marker));
    }
    return this;
  }

  addMarker(marker) {
    marker.html = marker.html || (() => `<div>${marker.id}</div>`);
    const hMarker = new window.H.map.DomMarker(marker);

    hMarker.addEventListener('tap', this.tapEventHandler.bind(this));
    hMarker.addEventListener('pointerenter', this.onMouseenterEventHandler.bind(this));
    hMarker.addEventListener('pointerleave', this.onMouseleaveEventHandler.bind(this));
    hMarker.handlers = [
      { event: 'tap', handler: this.tapEventHandler },
      { event: 'pointerenter', handler: this.onMouseenterEventHandler },
      { event: 'pointerleave', handler: this.onMouseleaveEventHandler }
    ];

    hMarker.setData(marker);

    /* if (marker.icon) {
      hMarker.setIcon(this.createDomIcon({ id: `${hMarker.getId()}`, placeholder: marker.icon }));
    } else if (this.placeholder) {
      hMarker.setIcon(this.createDomIcon({ id: `${hMarker.getId()}`, placeholder: this.placeholder }));
    }
 */
    this.setHMarkerIcon(hMarker);

    this.hMarkers.push(hMarker);
    this.markersAsObj[marker.id] = hMarker;

    let hMarkerPosition;
    if (this.usingApiKey === false) {
      hMarkerPosition = hMarker.getPosition();
    } else {
      hMarkerPosition = hMarker.getGeometry();
    }
    const bubble = new window.H.ui.InfoBubble(hMarkerPosition, { content: hMarker.getData().html() });
    bubble.setData({ marker: { id: marker.id, source: marker.source } });
    bubble.setState(window.H.ui.InfoBubble.State.CLOSED);
    hMarker.bubble = bubble;
    this.ui.addBubble(bubble);

    return this;
  }

  setHMarkerIcon(hMarker) {
    if (hMarker.getData().icon) {
      hMarker.setIcon(this.createDomIcon({ id: `${hMarker.getId()}`, placeholder: hMarker.getData().icon }));
    } else if (this.placeholder) {
      hMarker.setIcon(this.createDomIcon({ id: `${hMarker.getId()}`, placeholder: this.placeholder }));
    }
  }

  setMarkerIcon(markerId, icon) {
    const hMarker = this.getMarkerById(markerId);

    if (hMarker) {
      const currenticon = hMarker.getData().icon;
      if (currenticon !== icon) {
        hMarker.getData().icon = icon;
        this.setHMarkerIcon(hMarker);
      }
    } else {
      console.warn('no marker ' + markerId);
    }
  }

  getMarkerIcon(markerId) {
    const hMarker = this.getMarkerById(markerId);
    if (hMarker) {
      return hMarker.getData().icon;
    }
    return null;
  }

  removeMarker(marker) {
    const indexToBeDeleted = this.hMarkers.findIndex(hMarker => hMarker.getData().id === marker.id);
    this.hMarkers.splice(indexToBeDeleted, 1);
    delete this.markersAsObj[marker.id];
  }

  updateMarkerPosition(markerId, lat, lng) {
    const marker = this.getMarkerById(markerId);
    if (marker) {
      if (this.usingApiKey === false) {
        marker.setPosition(new window.H.geo.Point(lat, lng));
      } else {
        marker.setGeometry({ lat, lng });
      }
    } else {
      console.log('no marker');
    }
  }

  reverseGeocode = async ({ lat, lng, mode = 'retrieveAddresses', maxresults = 1 }) => {
    return await this.api.reverseGeocode(lat, lng, 0, mode);
  }

  geocode(location) {
    return this.api.geocode(location).then(({ Response }) => {
      if (Response.View[0] && Response.View[0].Result[0]) {
        const { Latitude: lat, Longitude: lng } = Response.View[0].Result[0].Location.DisplayPosition;
        const { Label: label } = Response.View[0].Result[0].Location.Address;
        const { MatchLevel: matchLevel } = Response.View[0].Result[0];
        const { BottomRight, TopLeft } = Response.View[0].Result[0].Location.MapView;

        const bottomLeftPoint = new window.H.geo.Point(BottomRight.Latitude, TopLeft.Longitude);
        const topLeftPoint = new window.H.geo.Point(TopLeft.Latitude, TopLeft.Longitude);
        const bottomRightPoint = new window.H.geo.Point(BottomRight.Latitude, BottomRight.Longitude);

        const width = Math.round(bottomLeftPoint.distance(bottomRightPoint));
        const height = Math.round(bottomLeftPoint.distance(topLeftPoint));

        return {
          lat,
          lng,
          matchLevel,
          label,

          mapView: {
            radius: Math.min(height, width),
            bottomRight: { lat: BottomRight.Latitude, lng: BottomRight.Longitude },
            topLeft: { lat: TopLeft.Latitude, lng: TopLeft.Longitude }
          }
        };
      }
      return {};
    });
  }

  suggest(searchText) {
    return this.api.autocomplete(searchText);
  }

  places(searchText, at) {
    return this.api.places(searchText, at);
  }

  getAddressFromReverseGeocode({ lat, lng }) {
    return this.reverseGeocode({ lat, lng });
  }

  createDomIcon({ id, placeholder }) {
    var domIcon = new window.H.map.DomIcon(`<div class="marker" id="marker_${id}">${placeholder}</div>`, {
      onAttach: function(clonedElement) {
        clonedElement.addEventListener('mouseover', mouseover);
        clonedElement.addEventListener('mouseout', mouseout);
        clonedElement.addEventListener('click', click);
      },
      onDetach: function(clonedElement) {
        clonedElement.removeEventListener('mouseover', mouseover);
        clonedElement.removeEventListener('mouseout', mouseout);
        clonedElement.removeEventListener('click', click);
      }
    });
    return domIcon;
  }

  showAllMarkers() {
    if (this.group) {
      this.map.removeObject(this.group);
    }
    this.group = new window.H.map.Group();
    this.group.addObjects(this.hMarkers.filter(m => !!m));
    this.map.addObject(this.group);
    // this.map.setViewBounds(this.group.getBounds());
    if (this.usingApiKey === false) {
      this.map.setViewBounds(this.group.getBounds());
    } else {
      this.map.getViewModel().setLookAtData({ bounds: this.group.getBoundingBox() });
    }
    return this;
  }

  showVisibleMarkers(setViewBound = true) {
    if (this.group) {
      this.map.removeObject(this.group);
    }
    this.group = new window.H.map.Group();
    this.group.addObjects(this.getVisibleMarker());
    this.map.addObject(this.group);
    if (setViewBound) {
      if (this.usingApiKey === false) {
        this.map.setViewBounds(this.group.getBounds());
      } else {
        if (this.group.getBoundingBox() !== null) {
          this.map.getViewModel().setLookAtData({ bounds: this.group.getBoundingBox() });
        }
      }
    }
    return this;
  }

  getMarkersWithMeta = meta => {
    const key = Object.keys(meta).shift();
    const markersWithMeta = this.hMarkers.filter(marker => marker.getData()[key] === meta[key]);
    return markersWithMeta;
  };

  getVisibleMarker() {
    return this.hMarkers.filter(hMarker => !!hMarker && hMarker.getVisibility() === true);
  }

  getNotVisibleMarker() {
    return this.hMarkers.filter(hMarker => hMarker.getVisibility() === false);
  }

  getMarkerByDataField({ field = 'id', value }) {
    let marker;
    for (let i = 0, len = this.hMarkers.length; i < len; i++) {
      if (this.hMarkers[i].getData()[field] === value) {
        marker = this.hMarkers[i];
        break;
      }
    }
    return marker;
    //return (markers || this.hMarkers).find(hMarker => hMarker.getData()[field] === value);
  }

  getMarkerById(id) {
    return this.markersAsObj[id];
  }

  showBubbleFor(hMarker) {
    hMarker.bubble.setState(window.H.ui.InfoBubble.State.OPEN);
    return this;
  }

  closeBubbles() {
    this.ui.getBubbles().forEach(this.closeBubble.bind(this));
    return this;
  }

  closeBubble(bubble) {
    bubble.setState(window.H.ui.InfoBubble.State.CLOSED);
    return this;
  }

  removeBubble(bubble) {
    this.ui.removeBubble(bubble);
    return this;
  }

  removeAllBubbles() {
    this.ui.getBubbles().forEach(this.removeBubble.bind(this));
    return this;
  }

  setBubbleContent = (bubble, content) => {
    bubble.setContent(content);
    return this;
  };

  addBubbleClass = (bubble, className) => {
    bubble.addClass(className);
    return this;
  };

  removeBubbleClass = (bubble, className) => {
    bubble.removeClass(className);
    return this;
  };

  showMarkerIfHidden(hMarker) {
    let hMarkerPosition;
    if (this.usingApiKey === false) {
      hMarkerPosition = hMarker.getPosition();
    } else {
      hMarkerPosition = hMarker.getGeometry();
    }
    // const isContained = this.map.getViewBounds().containsPoint(hMarkerPosition);
    const isContained = this.getViewBounds().containsPoint(hMarkerPosition);
    return new Promise(resolve => {
      if (!isContained) {
        // this.map.setViewBounds(this.map.getViewBounds().mergePoint(hMarkerPosition), true);
        if (this.usingApiKey === false) {
          this.map.setViewBounds(this.map.getViewBounds().mergePoint(hMarkerPosition), true);
        } else {
          this.map.getViewModel().setLookAtData({ bounds: this.group.getBoundingBox().mergePoint(hMarkerPosition) }, 75);
        }
        const interval = setInterval(() => {
          // if (this.map.getViewBounds().containsPoint(hMarkerPosition)) {
          if (this.getViewBounds().containsPoint(hMarkerPosition)) {
            setTimeout(() => {
              resolve(this);
            }, 100);
            clearInterval(interval);
          }
        }, 50);
      } else {
        resolve(this);
      }
    });
  }

  highlight(hMarker, highlight = true) {
    const element = document.getElementById(hMarker.getId());
    if (!!element) {
      if (highlight) {
        element.dispatchEvent(new MouseEvent('mouseover'));
      } else {
        element.dispatchEvent(new MouseEvent('mouseout'));
      }
    }
  }

  centerMarker(hMarker, zoom) {
    let hMarkerPosition;
    if (this.usingApiKey === false) {
      hMarkerPosition = hMarker.getPosition();
    } else {
      hMarkerPosition = hMarker.getGeometry();
    }
    this.map.setCenter(hMarkerPosition, false);
    if (zoom) {
      this.map.setZoom(zoom);
    }
    return this;
  }

  setZoom(zoom, animation = false) {
    this.map.setZoom(zoom, animation);
  }

  getCenter() {
    return pick(this.map.getCenter(), ['lat', 'lng']);
  }

  setCenter(lat, lng) {
    this.map.setCenter(new window.H.geo.Point(lat, lng));
  }

  setZIndex(hMarker, zIndex) {
    hMarker.setZIndex(zIndex);
    return this;
  }

  removeAllMarkers() {
    if (!!this.hMarkers && this.hMarkers.length > 0) {
      this.hMarkers.forEach(hMarker => {
        //hMarker.handlers.forEach(h => h.removeEventListener(h.event, h.handler));
      });
      this.removeAllBubbles();
      this.group.removeObjects(this.hMarkers);
      this.hMarkers = [];
    }
    return this;
  }

  tapEventHandler(evt) {
    if (isFunction(this.onTapCallback)) {
      this.onTapCallback(evt, this);
    }
  }

  onMouseenterEventHandler(evt) {
    if (isFunction(this.onPointerenterCallback)) {
      this.onPointerenterCallback(evt.target);
    }
  }

  onMouseleaveEventHandler(evt) {
    if (isFunction(this.onPointerleaveCallback)) {
      this.onPointerleaveCallback(evt.target);
    }
  }

  resize() {
    this.map.getViewPort().resize();
    return this;
  }

  addGeofenceDraggable(geofenceObject) {
    this.removeGeofenceDraggable();
    this.geofenceDraggableGroup = new window.H.map.Group({
      volatility: true
    });

    this.geofenceDraggableGroup.addObjects([geofenceObject]);

    this.map.addObject(this.geofenceDraggableGroup);

    this.geofenceDraggableGroup.addEventListener('pointerenter', function() {
      document.body.style.cursor = 'pointer';
    }, true);

    this.geofenceDraggableGroup.addEventListener('pointerleave', function() {
      document.body.style.cursor = 'default';
    }, true);

    // this.map.setViewBounds(this.geofenceDraggableGroup.getBounds());
    if (this.usingApiKey === false) {
      return this.map.setViewBounds(this.geofenceDraggableGroup.getBounds());
    } else {
      return this.map.getViewModel().setLookAtData({ bounds: this.geofenceDraggableGroup.getBoundingBox() });
    }

    return this;
  };

  removeGeofenceDraggable() {
    if (this.geofenceDraggableGroup) {
      try {
        this.map.removeObject(this.geofenceDraggableGroup);
      } catch (ex) {

      }
      this.geofenceDraggableGroup = undefined;
    }
  }

  geoToScreen(obj) {
    return this.map.geoToScreen(obj);
  }

  screenToGeo(x, y) {
    return this.map.screenToGeo(x, y);
  }

  /**
   *
   * @param {*list of point} points a point should be an object with lat and lng fields. example: {lat: 123, lng: 123}
   */
  addPolyline(points) {
    if (this.polyline) {
      this.map.removeObject(this.polyline);
    }

    if (points && points.length > 0) {
      var lineString = new window.H.geo.LineString();
      points.forEach(point => {
        lineString.pushPoint(point);
      });

      this.polyline = new window.H.map.Polyline(lineString, { style: { lineWidth: 4, strokeColor: '#49c6be' } });

      this.map.addObject(this.polyline);
      // this.map.setViewBounds(this.polyline.getBounds(), true);
      if (this.usingApiKey === false) {
        return this.map.setViewBounds(this.polyline.getBounds(), true);
      } else {
        return this.map.getViewModel().setLookAtData({ bounds: this.polyline.getBoundingBox() }, 10);
      }

      return this;
    }
  }

  addCircle(lat, lng, radius, style = {}) {
    const circle = new window.H.map.Circle(
      { lat, lng },

      radius,
      {
        style
      }
    );
    this.map.addObject(circle);

    return circle;
  }

  addPoint(lat, lng, style) {
    this.point = new window.H.map.Circle(
      { lat, lng },

      1000,
      {
        style
      }
    );
  }

  removeObject(object) {
    this.map.removeObject(object);
  }

  addObject = object => {
    this.map.addObject(object);
  };

  getBubbleWithMeta = meta => {
    const key = Object.keys(meta).shift();
    const bubblesWithMeta = this.ui.getBubbles().filter(bubble => bubble.getData().marker[key] === meta[key]);
    return bubblesWithMeta;
  };

  setViewBounds(object, animate = false) {
    if (this.usingApiKey === false) {
      return this.map.setViewBounds(object.getBounds(), animate);
    } else {
      return this.map.getViewModel().setLookAtData({ bounds: object.getBoundingBox() }, animate);
    }
  }

  getViewBounds() {
    if (this.usingApiKey === false) {
      return this.map.getViewBounds();
    } else {
      return this.map.getViewModel().getLookAtData().bounds.getBoundingBox();
    }
  }

  /**
   * returns the height of the map in meters
   */
  getHeight() {
    // const vb = this.map.getViewBounds();
    const vb = this.getViewBounds();
    const P1 = new window.H.geo.Point(vb.getTopLeft().lat, vb.getBottomRight().lng);
    const v2 = vb.getBottomRight()
    const v3 = v2.distance(P1);
    return v3;
  }

  /**
   * returns the width of the map in meters
   */
  getWidth() {
    // const vb = this.map.getViewBounds();
    const vb = this.getViewBounds();
    const P1 = new window.H.geo.Point(vb.getBottomRight().lat, vb.getTopLeft().lng);
    const v2 = vb.getBottomRight();
    const v3 = v2.distance(P1);
    return v3;
  }

  getZoom() {
    return this.map.getZoom();
  }

  isLandscape = () => {
    const width = this.map.getViewPort().width;
    const height = this.map.getViewPort().height;

    return width > height;
  };

  /**
   * return the short side of the viewport in pixel
   */
  getShortSide = () => {
    const width = this.map.getViewPort().width;
    const height = this.map.getViewPort().height;
    return Math.min(height, width);
  };

  getShortSideInMeter = () => {
    return Math.min(this.getHeight(), this.getWidth());
  };
}

export default Map;



// WEBPACK FOOTER //
// ./src/classes/Map.js